import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { OwnerRevenue } from "@/components/equipment/OwnerRevenue";
import { TransferReport } from "@/components/equipment/TransferReport";
import { DollarSign, PackageCheck, TrendingUp, Download, Trophy, AlertTriangle } from "lucide-react";

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Metric({ label, value, icon: Icon, accent, testid, tint }) {
  return (
    <div className={`${tint || "bg-white border border-zinc-200"} rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-600">{label}</div>
        <Icon size={18} className={accent} />
      </div>
      <div className="font-heading text-3xl font-black tracking-tighter text-brand-display mt-3 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function HireSummary({ showExport = false, showFilters = false, showOwners = false }) {
  const [s, setS] = useState(null);
  const [owners, setOwners] = useState([]);
  const [transfers, setTransfers] = useState({ routes: [], items: [] });
  const [locations, setLocations] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [filters, setFilters] = useState({ status: "all", start: "", end: "", location: "all", equipment: "all" });

  useEffect(() => {
    api.get("/my-locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
  }, []);

  const buildQuery = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") p.append(k, v);
    });
    return p.toString();
  };

  const load = useCallback((f) => {
    const params = f || filters;
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v && v !== "all") p.append(k, v);
    });
    api.get(`/hire/summary?${p.toString()}`).then((r) => setS(r.data)).catch(() => {});
    if (showOwners) api.get(`/hire/by-owner?${p.toString()}`).then((r) => setOwners(r.data)).catch(() => {});
    if (showOwners) api.get(`/hire/transfers?${p.toString()}`).then((r) => setTransfers(r.data)).catch(() => {});
  }, [filters, showOwners]);

  useEffect(() => {
    load();
    const id = setInterval(() => load(), 15000);
    return () => clearInterval(id);
  }, [load]);

  const download = async (path, filename, msg) => {
    try {
      const res = await api.get(`${path}?${buildQuery()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(msg);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const exportCsv = () => download("/hire/export", "hire_revenue_report.csv", "Hire report exported");
  const exportOwners = () => download("/hire/by-owner/export", "hire_revenue_by_owner.csv", "Owner report exported");
  const exportTransfers = () => download("/hire/transfers/export", "branch_transfers.csv", "Transfer report exported");

  if (!s) return null;

  return (
    <div data-testid="hire-summary">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">
          Equipment Hire Revenue
        </h2>
        {showExport && (
          <button
            onClick={exportCsv}
            data-testid="export-hire-button"
            className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-4 py-2 text-sm flex items-center gap-2"
          >
            <Download size={16} /> Export Hire CSV
          </button>
        )}
      </div>

      {showFilters && (
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 mb-6 grid grid-cols-1 sm:grid-cols-6 gap-3" data-testid="hire-filters">
          <div>
            <label className="text-xs font-bold text-zinc-600 block mb-1">Equipment</label>
            <input
              list="hire-equipment-options"
              value={filters.equipment === "all" ? "" : filters.equipment}
              onChange={(e) => setFilters({ ...filters, equipment: e.target.value || "all" })}
              data-testid="hire-equipment-filter"
              placeholder="Search equipment…"
              className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <datalist id="hire-equipment-options">
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.name} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 block mb-1">Location</label>
            <select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              data-testid="hire-location-filter"
              className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="all">All sites</option>
              {locations.map((l) => (
                <option key={l.id} value={l.name}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 block mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              data-testid="hire-status-filter"
              className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="returned">Returned</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 block mb-1">Start from</label>
            <input
              type="date"
              value={filters.start}
              onChange={(e) => setFilters({ ...filters, start: e.target.value })}
              data-testid="hire-start-filter"
              className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 block mb-1">Start to</label>
            <input
              type="date"
              value={filters.end}
              onChange={(e) => setFilters({ ...filters, end: e.target.value })}
              data-testid="hire-end-filter"
              className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => load()}
              data-testid="apply-hire-filters"
              className="bg-zinc-950 text-white rounded-full font-bold tracking-tight hover:bg-zinc-800 transition-colors px-5 py-2 text-sm"
            >
              Apply
            </button>
            {(filters.status !== "all" || filters.start || filters.end || filters.location !== "all" || filters.equipment !== "all") && (
              <button
                onClick={() => { const cleared = { status: "all", start: "", end: "", location: "all", equipment: "all" }; setFilters(cleared); load(cleared); }}
                data-testid="clear-hire-filters"
                className="text-zinc-500 hover:text-zinc-900 text-sm font-semibold px-2 py-2"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        <Metric label="Active Hires" value={s.total_active_hires} icon={PackageCheck} accent="text-brand" tint="bg-brand-card-blue" testid="hire-active" />
        <Metric label="Revenue This Month" value={money(s.revenue_this_month)} icon={DollarSign} accent="text-emerald-600" tint="bg-brand-card-sage" testid="hire-revenue-month" />
        <Metric label="Total Revenue" value={money(s.revenue_total)} icon={TrendingUp} accent="text-zinc-900" tint="bg-brand-card-cream" testid="hire-revenue-total" />
      </div>

      {s.overdue_count > 0 && (
        <div className="bg-red-50 border border-red-300 mb-6" data-testid="overdue-alert">
          <div className="px-4 py-3 border-b border-red-200 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <h3 className="font-heading text-lg font-bold tracking-tight text-red-800">
              {s.overdue_count} Overdue Hire{s.overdue_count > 1 ? "s" : ""}
            </h3>
            <span className="text-xs text-red-600 font-semibold">— past end date, awaiting return</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-red-700 font-bold border-b border-red-200">
                <th className="px-4 py-2">Equipment</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2">Due</th>
                <th className="px-4 py-2 text-right">Days Overdue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-red-100">
              {s.overdue_orders.map((o) => (
                <tr key={o.id} data-testid={`overdue-${o.id}`} className="hover:bg-red-100/40">
                  <td className="px-4 py-2 font-semibold text-red-900">{o.equipment_name}</td>
                  <td className="px-4 py-2 text-red-800">{o.customer}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-red-800">{o.quantity}</td>
                  <td className="px-4 py-2 text-red-700 text-xs">{o.end_date}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-red-700">{o.days_overdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)]" data-testid="hire-most-hired">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
          <Trophy size={16} className="text-amber-500" />
          <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-900">Most-Hired Equipment</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left">
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Equipment</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Orders</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Units</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {s.most_hired.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-400">No hire orders yet.</td></tr>
            ) : (
              s.most_hired.map((m) => (
                <tr key={m.equipment} className="hover:bg-zinc-50">
                  <td className="px-4 py-2 font-semibold text-zinc-900">{m.equipment}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{m.orders}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{m.units}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{money(m.revenue)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showOwners && <OwnerRevenue rows={owners} onExport={exportOwners} />}
      {showOwners && <TransferReport data={transfers} onExport={exportTransfers} />}
    </div>
  );
}
